import { useState, useEffect, useCallback, useRef } from 'react';
import { getNextAvailableColor, CHART_COLORS } from '../utils/colorPalette';
import {
    normalizeConfigFilterParams,
    normalizeFilterParams,
} from '../utils/filterParams';

const STORAGE_KEY = 'graph-builder-config';
const SOURCE_STORAGE_KEY = 'graph-builder-source';

const generateId = () => Math.random().toString(36).substr(2, 9);

const createNewSeries = (usedColors = []) => ({
    id: generateId(),
    dataSource: { type: 'dataset', value: '' },
    filterParams: [],
    serie: {
        property: '',
        label: '',
        color: getNextAvailableColor(usedColors),
        xAxisCategoryProperty: '',
        yAxisCategoryProperty: '',
        negateSerieValues: false,
        zone: { axis: '', startValue: '', endValue: '' },
    },
    annotations: [],
});

const getInitialConfig = () => ({
    chartType: null,
    title: '',
    displayLargeTitle: false,
    showTitleInToc: false,
    gridSpan: '1',
    includeInOverview: false,
    hideChartForCountries: [],

    yAxis: {
        label: '',
        min: null,
        max: null,
        tickInterval: null,
        stackValues: false,
    },
    xAxis: {
        label: '',
        categoryOrdering: 'asc',
        categorySortingType: 'number',
        labelFormatting: 'none',
    },

    series: [createNewSeries()],

    dynamicProperty: null,

    enableLineSymbols: false,
    stackingType: 'normal',
    polarChartValueType: 'line',
    layoutAlgorithm: 'sliceAndDice',
    hierarchyLevels: [],
    dateOrdering: 'asc',
    dateFormatting: 'none',
    spacePointsEvenly: false,

    previewMode: 'full',

    decimalPlaces: null,
});

export const useChartConfig = () => {
    const [config, setConfig] = useState(getInitialConfig);
    const [wagtailSource, setWagtailSource] = useState(null);
    const [hasSavedConfig, setHasSavedConfig] = useState(false);
    const savedConfigRef = useRef(null);
    const isInitialized = useRef(false);

    // Check for saved config on mount
    useEffect(() => {
        if (isInitialized.current) return;
        isInitialized.current = true;

        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                // Only show restore prompt if config has meaningful data
                const hasMeaningfulConfig =
                    parsed.chartType ||
                    parsed.title ||
                    parsed.series?.[0]?.dataSource?.value;
                if (hasMeaningfulConfig) {
                    savedConfigRef.current =
                        normalizeConfigFilterParams(parsed);
                    setHasSavedConfig(true);
                }
            }
        } catch (e) {
            console.error('Failed to load saved config:', e);
            localStorage.removeItem(STORAGE_KEY);
        }

        try {
            const savedSource = localStorage.getItem(SOURCE_STORAGE_KEY);
            if (savedSource) {
                setWagtailSource(JSON.parse(savedSource));
            }
        } catch (e) {
            localStorage.removeItem(SOURCE_STORAGE_KEY);
        }
    }, []);

    // Save to localStorage on every config change
    useEffect(() => {
        if (!isInitialized.current) return;

        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
        } catch (e) {
            console.error('Failed to save config:', e);
        }
    }, [config]);

    const updateConfig = useCallback((updates) => {
        setConfig((prev) => {
            if (typeof updates === 'function') {
                return updates(prev);
            }
            return { ...prev, ...updates };
        });
    }, []);

    const resetConfig = useCallback(() => {
        const fresh = getInitialConfig();
        setConfig(fresh);
        setWagtailSource(null);
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(SOURCE_STORAGE_KEY);
    }, []);

    const restoreSavedConfig = useCallback(() => {
        if (savedConfigRef.current) {
            setConfig(savedConfigRef.current);
        }
        setHasSavedConfig(false);
    }, []);

    const dismissSavedConfig = useCallback(() => {
        savedConfigRef.current = null;
        setHasSavedConfig(false);
        setWagtailSource(null);
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(SOURCE_STORAGE_KEY);
        const fresh = getInitialConfig();
        setConfig(fresh);
    }, []);

    const addSeries = useCallback(() => {
        const newId = generateId();
        setConfig((prev) => {
            const usedColors = prev.series
                .map((s) => s.serie?.color)
                .filter(Boolean);
            const newSeries = createNewSeries(usedColors);
            newSeries.id = newId;
            // Inherit dataset from first series if available
            if (prev.series.length > 0 && prev.series[0].dataSource?.value) {
                newSeries.dataSource = { ...prev.series[0].dataSource };
            }
            if (prev.chartType === 'mixed') {
                newSeries.seriesChartType = 'line';
            }
            return {
                ...prev,
                series: [...prev.series, newSeries],
            };
        });
        return newId;
    }, []);

    const removeSeries = useCallback((seriesId) => {
        setConfig((prev) => ({
            ...prev,
            series: prev.series.filter((s) => s.id !== seriesId),
        }));
    }, []);

    const updateSeries = useCallback((seriesId, updates) => {
        setConfig((prev) => ({
            ...prev,
            series: prev.series.map((s) =>
                s.id === seriesId
                    ? {
                          ...s,
                          ...updates,
                          serie: { ...s.serie, ...(updates.serie || {}) },
                      }
                    : s
            ),
        }));
    }, []);

    const duplicateSeries = useCallback((seriesId) => {
        const newId = generateId();
        setConfig((prev) => {
            const index = prev.series.findIndex((s) => s.id === seriesId);
            if (index === -1) return prev;
            const original = prev.series[index];
            const usedColors = prev.series
                .map((s) => s.serie?.color)
                .filter(Boolean);
            const clone = {
                ...original,
                id: newId,
                dataSource: { ...original.dataSource },
                filterParams: normalizeFilterParams(original.filterParams).map(
                    (f) => ({
                        ...f,
                    })
                ),
                serie: {
                    ...original.serie,
                    label: (original.serie?.label || '') + ' (copy)',
                    color: getNextAvailableColor(usedColors),
                },
                annotations: (original.annotations || []).map((a) => ({
                    ...a,
                })),
            };
            const newSeries = [...prev.series];
            newSeries.splice(index + 1, 0, clone);
            return { ...prev, series: newSeries };
        });
        return newId;
    }, []);

    const reorderSeries = useCallback((newSeriesOrder) => {
        setConfig((prev) => ({
            ...prev,
            series: newSeriesOrder,
        }));
    }, []);

    const importConfig = useCallback((importedConfig, sourceInfo = null) => {
        // Merge with defaults to ensure all fields exist
        const initial = getInitialConfig();
        const normalizedImportedConfig =
            normalizeConfigFilterParams(importedConfig);
        const merged = {
            ...initial,
            ...normalizedImportedConfig,
            yAxis: {
                ...initial.yAxis,
                ...(normalizedImportedConfig.yAxis || {}),
            },
            xAxis: {
                ...initial.xAxis,
                ...(normalizedImportedConfig.xAxis || {}),
            },
        };
        setConfig(merged);

        setWagtailSource(sourceInfo);
        if (sourceInfo) {
            localStorage.setItem(
                SOURCE_STORAGE_KEY,
                JSON.stringify(sourceInfo)
            );
        } else {
            localStorage.removeItem(SOURCE_STORAGE_KEY);
        }
    }, []);

    const clearWagtailSource = useCallback(() => {
        setWagtailSource(null);
        localStorage.removeItem(SOURCE_STORAGE_KEY);
    }, []);

    return {
        config,
        updateConfig,
        resetConfig,
        importConfig,
        hasSavedConfig,
        restoreSavedConfig,
        dismissSavedConfig,
        addSeries,
        removeSeries,
        duplicateSeries,
        updateSeries,
        reorderSeries,
        wagtailSource,
        clearWagtailSource,
    };
};
